<template>
  <div class="track-query-wrapper">
    <!-- 顶部：发件信息 → 运单状态 → 收件信息 路由概览卡片 -->
    <track-route-header :route-info="computedRouteInfo" />

    <!-- queryType === '1' 时显示 Tab（全部轨迹 + 地图轨迹） -->
    <div v-if="showMapTab" class="track-tabs-container">
      <div class="track-tabs-nav">
        <div
          :class="['track-tab-item', { active: activeTab === 'all' }]"
          @click="handleTabChange('all')"
        >
          <i class="el-icon-check tab-icon" />
          <span>{{ $t('trajectory.all') }} ({{ timelineList.length }})</span>
        </div>
        <div
          :class="['track-tab-item', { active: activeTab === 'map' }]"
          @click="handleTabChange('map')"
        >
          <i class="el-icon-map-location tab-icon" />
          <span>{{ $t('trajectory.trajectoryMap') }}</span>
        </div>
      </div>

      <!-- Tab 1: 全部时间轴轨迹 -->
      <div v-show="activeTab === 'all'" class="track-view-pane">
        <track-timeline
          :timeline-list="timelineList"
          @view-photo="handleViewPhoto"
          @view-problem-photo="handleViewProblemPhoto"
        />
      </div>

      <!-- Tab 2: 地图轨迹 -->
      <div v-if="hasLoadedMap" v-show="activeTab === 'map'" class="track-view-pane track-map-pane">
        <maps-trajectory :query-track-param="queryTrackParam" />
      </div>
    </div>

    <!-- queryType === '2' 时（例如在运单详情内部的轨迹Tab），直接展示时间轴 -->
    <div v-else class="track-single-view">
      <track-timeline
        :timeline-list="timelineList"
        @view-photo="handleViewPhoto"
        @view-problem-photo="handleViewProblemPhoto"
      />
    </div>

    <!-- 统一图片查看弹框（复用全局公共组件，效果与运单详情 100% 一致） -->
    <imageSeeDialog ref="imageSeeDialogRef" />
  </div>
</template>

<script>
import { getTrackQueryDetail } from '@/api/trackQuery';
import TrackRouteHeader from './trackRouteHeader.vue';
import TrackTimeline from './trackTimeline.vue';
import imageSeeDialog from '@/components/imageSeeDialog/index.vue';

/**
 * 物流轨迹查询容器组件
 *
 * 核心功能：
 * 1. 视图模式切换：支持独立弹窗模式（queryType === '1'，含【全部轨迹】与【轨迹地图】两个 Tab 切换并按需加载地图）
 *    及嵌套展示模式（queryType === '2'，常用于运单详情内部的轨迹面板，仅展示时间轴）；
 * 2. 路由卡片：顶部展示发件地（城市/州/邮编）→ 运单当前状态徽标 → 收件地（城市/州/邮编）全流程路由概览；
 * 3. 轨迹数据获取：根据传入的运单号调用后端统一接口 `getTrackQueryDetail`，一次性获取轨迹列表与路由详情；
 * 4. 图片弹框集成：统一接入全站公共弹框 `imageSeeDialog`，支持七牛云高清缩略图参数（imageMogr2）格式化，
 *    并针对【查看签收图片】与【查看问题件图片】传递动态标题。
 */
export default {
  name: 'TrackQuery',
  components: {
    TrackRouteHeader,
    TrackTimeline,
    imageSeeDialog,
    MapsTrajectory: () => import('@/views/orderCenter/logisticsOrders/components/MapsTrajectory/index')
  },
  props: {
    /**
     * 轨迹查询入参对象
     * @property {string} waybillNumber 运单号（必填）
     * @property {string} [senderCity] 发件城市（兜底）
     * @property {string} [senderState] 发件州代码（兜底）
     * @property {string} [senderZipcode] 发件邮编（兜底）
     * @property {string} [receiveCity] 收件城市（兜底）
     * @property {string} [receiveState] 收件州代码（兜底）
     * @property {string} [receiveZipcode] 收件邮编（兜底）
     * @property {string} [waybillStatusName] 运单状态名称（兜底）
     * @property {string|number} [waybillStatus] 运单状态编码（兜底）
     */
    queryTrackParam: {
      type: Object,
      default: () => ({})
    },
    /**
     * 查询模式：
     * '1': 独立主弹窗模式（含 Tab 导航：全部轨迹 + 轨迹地图）
     * '2': 详情内嵌模式（纯时间轴展示，不显示 Tab 与地图）
     */
    queryType: {
      type: String,
      default: '1'
    }
  },
  data() {
    return {
      /**
       * 当前激活的 Tab 选项（'all' 全部轨迹 | 'map' 地图轨迹）
       */
      activeTab: 'all',
      /**
       * 地图组件是否已首次激活并完成懒加载
       */
      hasLoadedMap: false,
      /**
       * 轨迹时间轴节点列表数据
       */
      timelineList: [],
      /**
       * 接口返回的运单详情与路由信息对象
       */
      trackDetailData: null
    };
  },
  computed: {
    /**
     * 是否显示地图 Tab 选项（仅在主弹窗模式 queryType === '1' 时显示）
     */
    showMapTab() {
      return String(this.queryType) === '1';
    },
    /**
     * 聚合计算路由概览卡片所需的数据（接口返回数据优先，入参数据兜底）
     */
    computedRouteInfo() {
      const data = this.trackDetailData || this.queryTrackParam || {};
      return {
        senderCity: data.senderCity || '',
        senderState: data.senderState || '',
        senderZipcode: data.senderZipcode || '',
        receiveCity: data.receiveCity || '',
        receiveState: data.receiveState || '',
        receiveZipcode: data.receiveZipcode || '',
        status: data.waybillStatusName || '',
        statusCode: data.waybillStatus !== undefined && data.waybillStatus !== null ? String(data.waybillStatus) : ''
      };
    }
  },
  /**
   * 组件创建钩子：初始化时发起轨迹数据请求
   */
  created() {
    this.fetchTrackData();
  },
  methods: {
    /**
     * 切换轨迹 Tab（全部轨迹 / 轨迹地图）
     * @param {string} tabKey 选中的 Tab 标识（'all' | 'map'）
     */
    handleTabChange(tabKey) {
      this.activeTab = tabKey;
      if (tabKey === 'map') {
        this.hasLoadedMap = true;
      }
    },
    /**
     * 调用后端接口拉取运单轨迹及路由信息
     */
    fetchTrackData() {
      const waybillNumber = this.queryTrackParam?.waybillNumber;
      if (!waybillNumber) {
        this.timelineList = [];
        this.trackDetailData = null;
        return;
      }

      getTrackQueryDetail(waybillNumber)
        .then((res) => {
          if (res && res.code === 200 && res.data) {
            this.trackDetailData = res.data;
            this.timelineList = Array.isArray(res.data.trackList) ? res.data.trackList : [];
          } else {
            this.timelineList = [];
            this.trackDetailData = null;
          }
        })
        .catch((err) => {
          console.error('[TrackQuery] getTrackQueryDetail error:', err);
          this.timelineList = [];
          this.trackDetailData = null;
        });
    },
    /**
     * 格式化七牛云图片 URL，附加高清缩略图处理参数（防止重复添加）
     * @param {string} url 原始图片 URL
     * @returns {string} 格式化后的图片 URL
     */
    formatImageUrl(url) {
      if (!url || typeof url !== 'string') return '';
      const trimmed = url.trim();
      if (!trimmed) return '';
      if (trimmed.includes('imageMogr2')) {
        return trimmed;
      }
      return trimmed + '?imageMogr2/thumbnail/2000x2000>/quality/80/format/jpg/ignore-error/1';
    },
    /**
     * 处理【查看签收图片】点击事件
     * 解析并合并当前节点的电子签名照片（deliverdNamePhoto）与现场签收照片（deliverdPhoto），
     * 格式化图片地址后通过全局公共弹框 imageSeeDialog 展示
     * @param {Object} item 当前轨迹单项对象
     */
    handleViewPhoto(item) {
      const urls = [];
      if (item.deliverdNamePhoto) {
        try {
          const names = typeof item.deliverdNamePhoto === 'string' ? JSON.parse(item.deliverdNamePhoto) : item.deliverdNamePhoto;
          if (Array.isArray(names)) {
            urls.push(...names);
          } else if (names) {
            urls.push(names);
          }
        } catch (e) {
          urls.push(item.deliverdNamePhoto);
        }
      }

      if (item.deliverdPhoto) {
        try {
          const photos = typeof item.deliverdPhoto === 'string' ? JSON.parse(item.deliverdPhoto) : item.deliverdPhoto;
          if (Array.isArray(photos)) {
            urls.push(...photos);
          } else if (photos) {
            urls.push(photos);
          }
        } catch (e) {
          urls.push(item.deliverdPhoto);
        }
      }

      const formattedList = urls.map(u => this.formatImageUrl(u)).filter(Boolean);
      if (formattedList.length > 0 && this.$refs.imageSeeDialogRef) {
        this.$refs.imageSeeDialogRef.showImageFun(formattedList, this.$t('trajectory.signImageTitle'));
      }
    },
    /**
     * 处理【查看问题件图片】点击事件
     * 解析并提取当前节点的异常件照片（exceptionImageUrls / problemItemImg），
     * 格式化图片地址后通过全局公共弹框 imageSeeDialog 展示
     * @param {Object} item 当前轨迹单项对象
     */
    handleViewProblemPhoto(item) {
      const urls = [];
      const rawPhotos = item.exceptionImageUrls || item.problemItemImg;
      if (rawPhotos) {
        if (Array.isArray(rawPhotos)) {
          rawPhotos.forEach(p => {
            if (p && String(p).trim()) {
              urls.push(String(p).trim());
            }
          });
        } else if (typeof rawPhotos === 'string') {
          const trimmed = rawPhotos.trim();
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              parsed.forEach(p => {
                if (p && String(p).trim()) {
                  urls.push(String(p).trim());
                }
              });
            } else if (parsed) {
              urls.push(String(parsed).trim());
            }
          } catch (e) {
            trimmed.split(',').forEach(p => {
              if (p && p.trim()) {
                urls.push(p.trim());
              }
            });
          }
        }
      }

      const formattedList = urls.map(u => this.formatImageUrl(u)).filter(Boolean);
      if (formattedList.length > 0 && this.$refs.imageSeeDialogRef) {
        this.$refs.imageSeeDialogRef.showImageFun(formattedList, this.$t('trajectory.problemImageTitle'));
      }
    }
  }
};
</script>

<style scoped>
.track-query-wrapper {
  padding: 12px 20px 24px;
  background: #ffffff;
  min-height: 100%;
  box-sizing: border-box;
}

.track-tabs-container {
  margin-top: 8px;
}

.track-tabs-nav {
  display: flex;
  align-items: center;
  border-bottom: 1px solid #E5E6EB;
  margin-bottom: 16px;
}

.track-tab-item {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 18px;
  font-size: 14px;
  color: #4E5969;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  transition: all 0.2s ease;
  user-select: none;
}

.track-tab-item:hover {
  color: #00B578;
}

.track-tab-item.active {
  color: #00B578;
  font-weight: 600;
  border-bottom-color: #00B578;
}

.track-tab-item .tab-icon {
  font-size: 14px;
}

.track-view-pane {
  min-height: 200px;
}

.track-map-pane {
  padding-top: 8px;
}

.track-single-view {
  margin-top: 12px;
}
</style>
